import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Uuid, type OrderView } from "@townsy/contracts";
import { errorMessage, isApiError } from "../../api/client";
import { postOrderCancel } from "../../api/endpoints";
import { queryKeys, useBootstrapQuery, useOrderQuery } from "../../api/queries";
import { resumeCheckout } from "../../commerce/checkoutTargets";
import { describeCommerceError } from "../../commerce/errors";
import { canCancel, canPayNow, isSettled, orderPollDelayMs } from "../../commerce/orderStatus";
import { useTownStore } from "../../state/store";
import { PageShell } from "../../ui/PageShell";
import { ReceiptInteriorCallout, useOwnStoreRead } from "../../ui/StoreThemeHost";
import { ReceiptStoreSetupCallout } from "../../ui/storeSetup/StoreSetupEntry";
import { themePickerAccess } from "../../ui/storeThemeLogic";
import { useOrderEffects } from "../../ui/checkout/CheckoutHost";
import { OrderStatusView } from "../../ui/checkout/OrderStatusView";
import { useServerNow } from "../../ui/checkout/useServerNow";

/** Query parameters Stripe may append on a redirect; the client secret is removed from the address bar. */
const STRIPE_RETURN_PARAMS = ["payment_intent", "payment_intent_client_secret", "redirect_status"];

/**
 * `/orders/:id` — the receipt (ADR-0007 #13): receipt number, amounts by funding source, test-mode
 * label, and the live state with the server's message, card payment status and refund status.
 * It polls while the order is moving. Arriving here from a payment redirect (`?return=1` or
 * Stripe's parameters) is only a hint: the page shows what the server says, never the redirect's
 * claimed status.
 */
export function OrderPage() {
  const { id } = useParams<{ id: string }>();
  const valid = Uuid.safeParse(id).success;
  const boot = useBootstrapQuery();
  const signedIn = Boolean(boot.data?.session.userId);
  const [search, setSearch] = useSearchParams();
  const [returned] = useState(() => search.has("return") || STRIPE_RETURN_PARAMS.some((k) => search.has(k)));
  useEffect(() => {
    if (STRIPE_RETURN_PARAMS.some((k) => search.has(k))) setSearch(new URLSearchParams({ return: "1" }), { replace: true });
  }, [search, setSearch]);

  const attempt = useRef(0);
  const q = useOrderQuery(valid ? (id as string) : null, signedIn, (query) => {
    const data = query.state.data;
    if (!data) return query.state.error ? false : 3000;
    const d = orderPollDelayMs(data.status, Math.min(attempt.current++, 5));
    return d === false ? false : Math.max(2000, d);
  });
  const order = q.data ?? null;
  const onOrder = useOrderEffects();
  useEffect(() => {
    if (order) onOrder(order);
  }, [order, onOrder]);
  const now = useServerNow(Boolean(order && !isSettled(order.status)));

  return (
    <PageShell title="Order receipt" narrow>
      {!valid ? (
        <div className="notice notice-danger" role="alert">
          <code>{id}</code> is not a Townsy order number. See <Link to="/orders">My orders</Link>.
        </div>
      ) : boot.isPending ? (
        <p className="muted">Loading your session…</p>
      ) : !signedIn ? (
        <div className="notice notice-info">
          <Link to="/account">Sign in</Link> to see this order. Orders are visible only to the account that placed them.
        </div>
      ) : q.isPending ? (
        <p className="muted">Loading the order…</p>
      ) : !order ? (
        <div className="notice notice-danger" role="alert">
          {isApiError(q.error) && q.error.code === "NOT_FOUND"
            ? "This order was not found for your account."
            : `Couldn't load this order: ${errorMessage(q.error)}`}{" "}
          <button type="button" className="btn btn-sm" onClick={() => void q.refetch()}>
            Retry
          </button>
        </div>
      ) : (
        <OrderDetails order={order} now={now} returned={returned} refreshing={q.isFetching} />
      )}
      <p style={{ marginTop: 20 }}>
        <Link to="/orders">My orders</Link> · <Link to="/">Back to the town</Link> · <Link to="/directory">Directory</Link>
      </p>
    </PageShell>
  );
}

/** A completed store purchase: the primary next step, "Set up your store" (ADR-0020); disabled with the reason for a former owner. */
function SetUpStore({ propertyId }: { propertyId: string }) {
  const openStoreSetup = useTownStore((s) => s.openStoreSetup);
  const own = useOwnStoreRead(propertyId);
  const access = themePickerAccess({ property: own.data, signedIn: true, fetching: own.isFetching, error: own.error });
  const blocked = access.state === "not_owner" || access.state === "not_store" || access.state === "sign_in";
  return <ReceiptStoreSetupCallout blocked={blocked} reason={blocked ? "Only the store's current owner can set it up." : null} onOpen={() => openStoreSetup(propertyId, "purchase")} />;
}

/** A completed store purchase: the prominent "Choose your store's interior" step (ADR-0008 #4). */
function ChooseInterior({ propertyId }: { propertyId: string }) {
  const openThemePicker = useTownStore((s) => s.openThemePicker);
  const own = useOwnStoreRead(propertyId);
  const access = themePickerAccess({ property: own.data, signedIn: true, fetching: own.isFetching, error: own.error });
  return <ReceiptInteriorCallout access={access} currentTheme={own.data?.interiorTheme ?? null} onChoose={() => openThemePicker(propertyId, "purchase")} />;
}

function OrderDetails({ order, now, returned, refreshing }: { order: OrderView; now: number; returned: boolean; refreshing: boolean }) {
  const openCheckout = useTownStore((s) => s.openCheckout);
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const settled = isSettled(order.status);
  const payable = canPayNow(order, now);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      const after = await postOrderCancel(order.orderId);
      qc.setQueryData(queryKeys.order(order.orderId), after);
    } catch (err) {
      setError(describeCommerceError(err, { stage: "cancel", kind: order.kind }).message);
    } finally {
      setBusy(false);
      setConfirmCancel(false);
      void qc.invalidateQueries({ queryKey: queryKeys.order(order.orderId) });
    }
  };

  return (
    <div className="stack" data-testid="order-page">
      {returned && !settled ? (
        <div className="notice notice-info" role="status">
          Back from the card check. Townsy is confirming the payment with the payment provider; this page updates by itself.
        </div>
      ) : null}
      {order.status === "fulfilled" && order.kind === "COMMERCIAL_ACQUISITION" && order.propertyId ? (
        <>
          <SetUpStore propertyId={order.propertyId} />
          <ChooseInterior propertyId={order.propertyId} />
        </>
      ) : null}
      <OrderStatusView order={order} now={now} />
      {!settled ? (
        <p className="small muted" role="status" style={{ margin: 0 }}>
          {refreshing ? "Checking for updates…" : "This page updates automatically."}
        </p>
      ) : null}
      {error ? (
        <div className="notice notice-danger" role="alert">
          {error}
        </div>
      ) : null}
      {payable || canCancel(order) ? (
        <div className="row">
          {payable ? (
            <button type="button" className="btn btn-primary" onClick={() => openCheckout(resumeCheckout(order.orderId))} data-testid="order-continue-payment">
              Continue to payment
            </button>
          ) : null}
          {canCancel(order) && !confirmCancel ? (
            <button type="button" className="btn" onClick={() => setConfirmCancel(true)} data-testid="order-cancel">
              Cancel order
            </button>
          ) : null}
        </div>
      ) : null}
      {confirmCancel ? (
        <div className="notice notice-warning stack" style={{ gap: 6 }}>
          <span>
            Cancel this order? The hold{order.creditAppliedCents > 0 ? " and your Townsy Credit" : ""} will be released. If the card payment already went
            through, the purchase completes instead.
          </span>
          <div className="row">
            <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void cancel()} data-testid="order-cancel-confirm">
              {busy ? "Canceling…" : "Yes, cancel"}
            </button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => setConfirmCancel(false)}>
              Keep it
            </button>
          </div>
        </div>
      ) : null}
      {order.status === "fulfilled" && order.propertyId ? (
        <p className="small" style={{ margin: 0 }}>
          <Link to={`/place/${order.propertyId}`}>See it in the town</Link>
        </p>
      ) : null}
    </div>
  );
}
